/*
 The MIT License (MIT)

 Copyright (c) 2017-2018 Stefano Cappa (Ks89)

 Permission is hereby granted, free of charge, to any person obtaining a copy
 of this software and associated documentation files (the "Software"), to deal
 in the Software without restriction, including without limitation the rights
 to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 copies of the Software, and to permit persons to whom the Software is
 furnished to do so, subject to the following conditions:

 The above copyright notice and this permission notice shall be included in all
 copies or substantial portions of the Software.

 THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 FITNESS FOR A PARTICULAR PURPOSE AND NON INFRINGEMENT. IN NO EVENT SHALL THE
 AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 SOFTWARE.
 */

import { Injectable, ModuleWithProviders, NgModule } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";

import { DIRECTIVES } from "./directives/directives";
import {
  CarouselComponent,
  COMPONENTS,
  ModalGalleryComponent,
} from "./components/components";
import {
  KEYBOARD_CONFIGURATION,
  KeyboardService,
} from "./services/keyboard.service";
import { KeyboardServiceConfig } from "./model/keyboard-service-config.interface";
import {
  HAMMER_GESTURE_CONFIG,
  HammerGestureConfig,
} from "@angular/platform-browser";
import {
  FaIconLibrary,
  FontAwesomeModule,
} from "@fortawesome/angular-fontawesome";
import { TranslateModule } from "@ngx-translate/core";
import { faPaperclip } from "@fortawesome/free-solid-svg-icons";
import {
  DevExtremeModule,
  DxButtonModule,
  DxPopupModule,
} from "devextreme-angular";
import {
  NgxDropzoneComponent,
  NgxDropzoneImagePreviewComponent,
  NgxDropzoneLabelDirective,
  NgxDropzoneModule,
  NgxDropzonePreviewComponent,
  NgxDropzoneRemoveBadgeComponent,
  NgxDropzoneVideoPreviewComponent,
} from "ngx-dropzone";
import { DxSharedModule } from "@app/shared/dx-shared.module";
import { NgxLoadingModule } from "ngx-loading";
import { DragulaModule } from "ng2-dragula";
import { NgxSkeletonLoaderModule } from "ngx-skeleton-loader";
import { ItemVideoModalGalleryComponent } from "./components/current-image/item-video-modal-gallery/item-video-modal-gallery.component";
import { ItemAudioModalGalleryComponent } from "./components/current-image/item-audio-modal-gallery/item-audio-modal-gallery.component";
import { EditorComponent } from "./components/editor/editor.component";
// import { NgxDropzoneService } from "ngx-dropzone/lib/ngx-dropzone.service";

// to prevent bad scrolling behaviour on mobile phone with carousels.
// From @mohaxspb (https://github.com/Ks89/angular-modal-gallery/pull/187)
@Injectable()
export class KsHammerGestureConfig extends HammerGestureConfig {
  buildHammer(element: HTMLElement) {
    return new Hammer(element, {
      touchAction: "pan-y",
    });
  }
}
function setupRouter(injector: KeyboardServiceConfig) {
  return new KeyboardService(injector);
}
/**
 * Module to import it in the root module of your application.
 */
@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    FontAwesomeModule,
    TranslateModule,
    DevExtremeModule,
    DxPopupModule,
    DevExtremeModule,
    FontAwesomeModule,
    TranslateModule,
    NgxDropzoneModule,
    DxButtonModule,

    DxSharedModule,
    NgxLoadingModule,
    DragulaModule.forRoot(),

    NgxSkeletonLoaderModule,
    NgxDropzoneModule,
    ItemVideoModalGalleryComponent,
    ItemAudioModalGalleryComponent,
    EditorComponent,
  ],
  declarations: [COMPONENTS, DIRECTIVES],
  exports: [ModalGalleryComponent, CarouselComponent, TranslateModule],
  providers: [
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: KsHammerGestureConfig,
    },
    {
      provide: KeyboardService,
      useFactory: setupRouter,
      deps: [KEYBOARD_CONFIGURATION],
    },
    {
      provide: KEYBOARD_CONFIGURATION,
      useValue: {},
    },
    NgxDropzoneComponent,
    NgxDropzoneImagePreviewComponent,
    NgxDropzoneLabelDirective,
    NgxDropzonePreviewComponent,
    NgxDropzoneRemoveBadgeComponent,
    NgxDropzoneVideoPreviewComponent,
  ],
})
export class GalleryModule {
  constructor(library: FaIconLibrary) {
    library.addIcons(faPaperclip);
  }
  /**
   * Importing with '.forRoot()' is deprecated. I'll provide a new way to do this in next releases.
   * Will be removed in 8.0.0 or 9.0.0
   */
  static forRoot(
    config?: KeyboardServiceConfig
  ): ModuleWithProviders<GalleryModule> {
    return {
      ngModule: GalleryModule,
      providers: [
        {
          provide: KEYBOARD_CONFIGURATION,
          useValue: config ? config : {},
        },
        {
          provide: KeyboardService,
          useFactory: setupKeyboardService,
          deps: [KEYBOARD_CONFIGURATION],
        },
      ],
    };
  }
}

/**
 * Function to setup the keyboard service inside the `forRoot` method.
 * @param KeyboardServiceConfig injector is an interface of type `KeyboardServiceConfig` to pass data to KeyboardService
 * @returns KeyboardService an instance of the `KeyboardService`
 */
export function setupKeyboardService(
  injector: KeyboardServiceConfig
): KeyboardService {
  return new KeyboardService(injector);
}
